/**
 * IL GIOCO SELEZIONATO, VISTO DA LIMULO — quale gioco del catalogo (`GET /v1/games`) corrisponde al gioco del selettore
 * della TMA, e quindi quale `?gameId=` chiedere alla lista dei tornei (I-342, Luca 2026-10-04 ~13:2xZ: «Nelle sezioni /
 * liste tornei continuo a vedere la lista dei tornei di tutti i giochi - voglio vedere lista solo dei tornei del gioco
 * selezionato»).
 *
 * ## Perche' serve una mappa
 * Il selettore (`useGameSelection`) ragiona sull'id NUMERICO della TMA — quello del monolite per Clash Royale
 * (`CR_GAME_ID = 6`) e i sintetici `9990xx` per i giochi che il monolite non ha (`lib/gameFeatures.ts`). I tornei Limulo
 * portano invece il `gameId` DELL'ORGANIZZAZIONE (`"1"` = Clash Royale su staging, `"100"` = backgammon), che non e' un
 * nome del gioco: il nome sta in `profileType` (v. il cappello di `openGame.ts`). L'unico ponte onesto e' quindi
 * id della TMA → `profileType` (QUI, una riga per gioco) → `gameId` del catalogo (`gameIdsOfProfileType`, `code.ts`).
 * ⛔ Nessun ripiego sul nome o su un `gameId` cablato (stessa regola di `backgammonGameIdsOf`): funzionerebbe finche'
 * il catalogo ha un gioco per tipo, e sbaglierebbe senza sintomi il giorno dopo.
 *
 * ## Cosa si chiede, e cosa si dice (le regole di tma-orcb, 13:24:07Z e 13:27:20Z)
 *  - catalogo non ancora arrivato ⇒ si ASPETTA: mai la lista di tutti i giochi come ripiego;
 *  - catalogo in errore ⇒ la lista non si chiede (lo stato degradato che esiste, mai un vuoto);
 *  - gioco senza `profileType` (scacchi, Clash of Clans, chinese poker: `null` ESPLICITO qui sotto), o catalogo letto
 *    senza quel tipo ⇒ la lista non si chiede, e il vuoto lo NOMINA: «Per {{game}} non ci sono ancora tornei.»;
 *  - UN `gameId` ⇒ `?gameId=` lato SERVER: pagine piene del gioco, «Carica altri» vero. Un filtro lato client su una
 *    pagina da 50 righe miste perderebbe tornei;
 *  - PIU' `gameId` per lo stesso tipo ⇒ la lista NON si legge: `?gameId=` prende un valore solo (`filtriDellaLista`,
 *    `qlash-limulo` `app-qtournaments/app/superficie/capability/tornei.js:802-804` @ `5baeedf`), e una frase di vuoto li'
 *    sarebbe falsa. Stato degradato, che non afferma niente. Oggi nessun catalogo lo produce;
 *  - nessun gioco selezionato ⇒ la lista di tutti: e' la scelta esplicita «Mostra tutti» del selettore
 *    (`GamePicker`, `clearGame`), non un ripiego su un dato mancante;
 *  - ⚠️ MA un gioco salvato e non ancora reidratato (`useGameSelection` lo rimette nello store in un effetto, DOPO il
 *    primo render) non e' «nessun gioco»: e' un'attesa. Senza questa riga, aprendo direttamente `/limulo/tournaments`
 *    partiva una lettura di tutti i giochi prima di quella del gioco (misurato in rete finta, 04/10 13:36Z).
 */
import {
  BACKGAMMON_GAME_ID,
  BRIDGE_GAME_ID,
  BRISCOLA_GAME_ID,
  TRESSETTE_GAME_ID,
  PILOTTA_GAME_ID,
  MPIRIMPA_GAME_ID,
  BURRACO_GAME_ID,
  CHESS_GAME_ID,
  CHINESE_POKER_GAME_ID,
  COC_GAME_ID,
  CR_GAME_ID,
  SCOPA_GAME_ID,
  SOLITARIO_GAME_ID,
} from '../gameFeatures.ts';
import { BACKGAMMON_PROFILE_TYPE } from '../qgammon/lobbyTornei.ts';
import { CODE_GAME_NAMES, CR_PROFILE_TYPE, gameIdsOfProfileType } from './code.ts';

/**
 * Il `profileType` degli scacchi nel catalogo Limulo: `qlash-limulo` `packages/app-qchess/README.md:118` @ `a3f759c` (staging).
 * Dal 06/10 (E6 di ADR-0193, P3): prima era `null` qui sotto. ⚠️ Il `gameId` NO: `INNESTO.md:114` dice `'chess'`, il catalogo
 * di staging risponde `"110"` (misura di qchess-orcr, 07/10 00:0xZ) — ed e' per questo che qui si mappa il TIPO, e il `gameId`
 * lo cerca `gameIdsOfProfileType` nel catalogo.
 */
export const CHESS_PROFILE_TYPE = 'chess';

/** Il `profileType` del burraco nel catalogo Limulo: `PROFILE_TYPE` di QBurraco `limulo-app/manifest.js:78` @ `83ab2d23`. */
export const BURRACO_PROFILE_TYPE = 'burraco';

/**
 * Il `profileType` della briscola nel catalogo Limulo: il valore di INNESTO.md §14 di QBriscola (`profileType` `briscola`), NON ancora letto da un catalogo (l'app `qbriscola` non e attiva
 * su Limulo staging: qlash-limulo#248). Finche il catalogo non ha quel tipo la lista dei tornei non si chiede e il vuoto nomina il gioco; ranked e tornei sono P9, fuori da questa PR.
 */
export const BRISCOLA_PROFILE_TYPE = 'briscola';

/**
 * Il `profileType` del tressette nel catalogo Limulo: il valore di INNESTO.md di QTressette (`profileType` `tressette`), NON ancora letto da un catalogo (l'app `qtressette` non e attiva su Limulo staging:
 * P5 del contratto). Finche il catalogo non ha quel tipo la lista dei tornei non si chiede e il vuoto nomina il gioco; ranked, tornei e code sono la faccia B, fuori da questa PR.
 */
export const TRESSETTE_PROFILE_TYPE = 'tressette';

/**
 * Il `profileType` della pilotta nel catalogo Limulo: il valore di INNESTO.md di QPilotta (`profileType` `pilotta`), NON ancora letto da un catalogo (l'app `qpilotta` non e attiva su Limulo staging:
 * P7 del contratto). Finche il catalogo non ha quel tipo la lista dei tornei non si chiede e il vuoto nomina il gioco; non ci sono tornei, ranked ne code (il contratto non li prevede).
 */
export const PILOTTA_PROFILE_TYPE = 'pilotta';

/**
 * Il `profileType` del Biriba nel catalogo Limulo: il valore di INNESTO.md di Qlash-IT/QMpirimpa (`mpirimpa`: «dominio: rotte BFF `/v1/games/mpirimpa/*`, codici `mpirimpa.sub.*`, `profileType`, `gameId`»),
 * NON ancora letto da un catalogo (l'app `qmpirimpa` non e' attiva su Limulo staging). Finche' il catalogo non ha quel tipo la lista dei tornei non si chiede e il vuoto nomina il gioco.
 */
export const MPIRIMPA_PROFILE_TYPE = 'mpirimpa';

/**
 * Il `profileType` della scopa nel catalogo Limulo: `PROFILE_TYPE` di Qlash-IT/QScopa @ ade9d5b838c24dbf3dad553c104b70ba54b8cc40, `limulo-app/gamekit/facciab.js:77-78` (`const PROFILE_TYPE = identita.dominio`) col valore in `limulo-app/gioco.js:10` (`dominio: 'scopa'`). NON ancora letto da un catalogo:
 * il mirror `app-qscopa` 0.3.0 (qlash-limulo#266) aspetta @phomea, e finche l'app non e servita su staging il catalogo non ha il tipo, la lista dei tornei non si chiede e la Hub dice «non disponibile» (caso 4). Giusto cosi.
 */
export const SCOPA_PROFILE_TYPE = 'scopa';

/**
 * L'id della TMA → il `profileType` del catalogo Limulo. `null` = quel gioco NON ha una voce nel catalogo (oggi:
 * Clash of Clans, chinese poker, bridge). Un id che non e' qui vale come `null`.
 * ⚠️ Gli scacchi hanno il loro tipo dal 06/10 (P3): con un catalogo che non li ha (l'app non attivata sull'organizzazione) la
 * lista non si chiede e il vuoto li nomina, come per ogni gioco fuori catalogo — non si ripiega su «tutti».
 */
export const PROFILE_TYPE_DEL_GIOCO: ReadonlyMap<number, string | null> = new Map<number, string | null>([
  [CR_GAME_ID, CR_PROFILE_TYPE],
  [BACKGAMMON_GAME_ID, BACKGAMMON_PROFILE_TYPE],
  [CHESS_GAME_ID, CHESS_PROFILE_TYPE],
  [COC_GAME_ID, null],
  [CHINESE_POKER_GAME_ID, null],
  // Il bridge non ha una voce nel catalogo Limulo (I-609): `null` ESPLICITO, il vuoto nomina il gioco.
  [BRIDGE_GAME_ID, null],
  // Il burraco SI: l'app `qburraco` si presenta ad `app-games` col `profileType` 'burraco' (QBurraco `limulo-app/manifest.js`, PROFILE_TYPE). Il `gameId`
  // lo cerca `gameIdsOfProfileType` nel catalogo (gli id non attraversano i sistemi): con un catalogo che non lo ha la lista non si chiede (caso 4).
  [BURRACO_GAME_ID, BURRACO_PROFILE_TYPE],
  // Il solitario non ha (ancora) tornei Limulo: `null` ESPLICITO, il vuoto nomina il gioco.
  [SOLITARIO_GAME_ID, null],
  // La briscola: il tipo di INNESTO §14 (v. `BRISCOLA_PROFILE_TYPE`); tornei e coda sono P9.
  [BRISCOLA_GAME_ID, BRISCOLA_PROFILE_TYPE],
  // Il tressette: il tipo di INNESTO (v. `TRESSETTE_PROFILE_TYPE`); tornei e code sono la faccia B.
  [TRESSETTE_GAME_ID, TRESSETTE_PROFILE_TYPE],
  // La pilotta: il tipo di INNESTO (v. `PILOTTA_PROFILE_TYPE`); nessun torneo.
  [PILOTTA_GAME_ID, PILOTTA_PROFILE_TYPE],
  // Il Biriba: il tipo di INNESTO (v. `MPIRIMPA_PROFILE_TYPE`); nessun torneo.
  [MPIRIMPA_GAME_ID, MPIRIMPA_PROFILE_TYPE],
  // La scopa: il tipo di Qlash-IT/QScopa @ ade9d5b838c24dbf3dad553c104b70ba54b8cc40, `limulo-app/gamekit/facciab.js:77-78` e `limulo-app/gioco.js:10` (v. `SCOPA_PROFILE_TYPE`); la scheda «Ranked» (la coda) e fuori, i tornei sono S7.
  [SCOPA_GAME_ID, SCOPA_PROFILE_TYPE],
]);

export function profileTypeDelGioco(activeGameId: number | null | undefined): string | null {
  if (activeGameId === null || activeGameId === undefined) return null;
  return PROFILE_TYPE_DEL_GIOCO.get(activeGameId) ?? null;
}

/** Cosa fare con la lista dei tornei per il gioco selezionato. */
export type ListaDelGioco =
  /** Nessun gioco selezionato: la lista di tutti (voce di cache «tutti»). */
  | { kind: 'tutti' }
  /** Il catalogo non e' ancora arrivato: si aspetta, la lista non si chiede. */
  | { kind: 'attesa' }
  /** Il catalogo e' in errore: la lista non si chiede, e non si afferma niente. */
  | { kind: 'catalogo-ko' }
  /** Il gioco non ha una voce nel catalogo: la lista non si chiede, e il vuoto nomina il gioco. */
  | { kind: 'fuori-catalogo'; nome: string }
  /** Piu' `gameId` per lo stesso tipo: la lista non si chiede (stato degradato). */
  | { kind: 'piu-id'; nome: string; gameIds: string[] }
  /** Il caso buono: si chiede `?gameId=`. */
  | { kind: 'gioco'; gameId: string; nome: string };

/**
 * @param activeGameId l'id della TMA (`useGameSelection().activeGameId`)
 * @param nomeSelettore il nome del gioco nel selettore (`activeGame.name`), per i giochi senza `profileType`
 * @param catalogo `GET /v1/games`: `undefined` = non ancora arrivato
 * @param catalogoInErrore la lettura del catalogo e' fallita senza mai rispondere
 */
export function listaDelGioco(input: {
  activeGameId: number | null | undefined;
  /** C'e' un gioco SALVATO che lo store non ha ancora (reidratazione in corso): si aspetta, non si chiede «tutti». */
  selezioneInArrivo?: boolean;
  nomeSelettore: string | null | undefined;
  catalogo: readonly { gameId: string; profileType: string }[] | undefined;
  catalogoInErrore: boolean;
}): ListaDelGioco {
  const { activeGameId, nomeSelettore, catalogo, catalogoInErrore } = input;
  if (activeGameId === null || activeGameId === undefined) return input.selezioneInArrivo ? { kind: 'attesa' } : { kind: 'tutti' };
  const tipo = profileTypeDelGioco(activeGameId);
  // Il nome e' un MARCHIO, uguale in ogni lingua (L-2645): quello delle code per CR/BG, quello del selettore per gli altri.
  const nome = (tipo !== null ? CODE_GAME_NAMES[tipo] : undefined) ?? nomeSelettore ?? '';
  if (tipo === null) return { kind: 'fuori-catalogo', nome };
  if (catalogo === undefined) return catalogoInErrore ? { kind: 'catalogo-ko' } : { kind: 'attesa' };
  const ids = gameIdsOfProfileType(catalogo, tipo);
  if (ids === null) return { kind: 'fuori-catalogo', nome };
  if (ids.size > 1) return { kind: 'piu-id', nome, gameIds: [...ids].sort() };
  return { kind: 'gioco', gameId: [...ids][0], nome };
}

/** Il `gameId` da chiedere, o `undefined` se la lista e' quella di tutti. `null` = la lista NON si chiede. */
export function gameIdDaChiedere(l: ListaDelGioco): string | undefined | null {
  if (l.kind === 'tutti') return undefined;
  if (l.kind === 'gioco') return l.gameId;
  return null;
}
